import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useHistoryStore } from '@/lib/store/history-store';
import { logAnalyticsEvent } from '@/lib/firebase/analytics';

export default function SuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutId = searchParams.get('checkout_id');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const { userId, setSubscribed } = useHistoryStore();

  useEffect(() => {
    document.title = 'Success - Deepcheck';
  }, []);

  useEffect(() => {
    if (!checkoutId) {
      setStatus('error');
      return;
    }

    // Verify the checkout with backend which will also sync to Firestore
    const verifyCheckout = async () => {
      try {
        const res = await fetch('/api/verify-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ checkoutId, userId }),
        });
        const data = await res.json();

        if (data.isValid) {
          setSubscribed(true, checkoutId, data.productId, data.customerEmail);
          logAnalyticsEvent('subscribe_success', { plan: data.productId });
          setStatus('success');
        } else {
          console.error('Subscription verification failed:', data.error);
          logAnalyticsEvent('subscribe_fail');
          setStatus('error');
        }
      } catch (err) {
        console.error('Verification error:', err);
        logAnalyticsEvent('subscribe_fail');
        setStatus('error');
      }
    };

    verifyCheckout();
  }, [checkoutId, userId, setSubscribed]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-sm text-center space-y-6">
        {status === 'loading' && (
          <>
            <div className="flex justify-center">
              <Loader2 className="w-12 h-12 text-green-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Finalizing Subscription...</h1>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Subscription Active!</h1>
            <p className="text-gray-600">
              Thank you for subscribing. You now have full access to Deepcheck.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full h-12 bg-black hover:bg-gray-800 text-white rounded-xl"
            >
              Start Searching
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
            <p className="text-gray-600">
              We couldn't verify your subscription details.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full h-12 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-xl"
            >
              Return Home
            </Button>
          </>
        )}
      </div>
    </main>
  );
}
